import React, { createRef, FC, Fragment, InputHTMLAttributes, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormikProps } from 'formik';

import { useAfterLayoutUpdate } from '@/common/utils';
import { Segment, SegmentChangeEvent, SegmentPasteEvent, SegmentProps, SegmentRef } from './components/segment';
import { styles } from './styles';

type ValueState = { [key: number]: string };

export type SegmentedInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onPaste' | 'form' | 'onFocus'> & {
  value: string;
  segments: Omit<SegmentProps, 'index' | 'onChange' | 'onPaste' | 'value'>[];
  onChange: (value: string) => void;
  onFocus?: () => void;
  divider?: string;
  form?: FormikProps<any>;
};

const parseValue = (value: string) => (value.replace(/[^A-Z]/gi, '') || '').toUpperCase();

const getInitialValue = ({ segments, value, startIndex = 0 }: Required<Pick<SegmentedInputProps, 'segments' | 'value'>> & { startIndex?: number }) => {
  const data = (segments || []).reduce((prev, curr, index) => ({ ...prev, [index]: '' }), {} as ValueState);
  const parsedValue = parseValue(value).split('');
  let index = startIndex;
  while (parsedValue.length > 0 && segments.length > index) {
    const segmentValue = parsedValue.splice(0, segments[index].maxLength);
    data[index] = segmentValue.join('');
    index = index + 1;
  }

  return data;
};

export const SegmentedInput: FC<SegmentedInputProps> = ({ name, value, segments, onChange, onFocus, divider = '-', form, ...props }) => {
  const [segmentsValue, setSegmentsValue] = useState<ValueState>(() => getInitialValue({ value, segments }));
  const segmentsRef = useRef<RefObject<SegmentRef>[]>([]);
  const isValueIntialized = useRef(false);
  const runAfterLayoutUpdate = useAfterLayoutUpdate();

  const handlePaste = useCallback<SegmentPasteEvent>(
    ({ index, nativeEvent }) => {
      nativeEvent.preventDefault();
      const newState = getInitialValue({ segments, value: nativeEvent.clipboardData.getData('Text') || '', startIndex: index });
      setSegmentsValue((oldState) => {
        const state = {} as ValueState;
        for (const key in oldState) {
          if (typeof newState[key] === 'string' && newState[key].length > 0) {
            state[key] = newState[key];
          } else {
            state[key] = oldState[key];
          }
        }

        return state;
      });
    },
    [segments]
  );

  const handleOnInputChange = useCallback<SegmentChangeEvent>(
    ({ index, maxLength, nativeEvent }) => {
      const { target } = nativeEvent;
      const newState = {} as ValueState;
      const nextSegment = segmentsRef.current[index + 1]?.current;
      const parsedValue = parseValue(target.value);
      const selection =
        typeof target.selectionStart === 'number' && typeof target.selectionEnd === 'number'
          ? target.selectionEnd > target.selectionStart
            ? target.selectionEnd
            : target.selectionStart
          : typeof target.selectionEnd === 'number'
          ? target.selectionEnd
          : typeof target.selectionStart === 'number'
          ? target.selectionStart
          : 0;

      if (selection <= maxLength) {
        newState[index] = parsedValue.slice(0, maxLength);
      } else if (parsedValue.length > maxLength && nextSegment) {
        newState[index + 1] = parsedValue.slice(maxLength);
      }

      setSegmentsValue((oldState) => ({
        ...oldState,
        ...newState,
      }));

      runAfterLayoutUpdate(() => {
        target.selectionStart = selection;
        target.selectionEnd = selection;
      });

      if (nextSegment !== undefined && selection !== undefined && selection >= maxLength) {
        nextSegment?.focus();
      }
    },
    [segments]
  );

  const segmentList = useMemo(
    () =>
      (segments || []).map((data, index) => {
        segmentsRef.current[index] = createRef<SegmentRef>();
        return (
          <Fragment key={`segment-control-${index}`}>
            {index !== 0 ? <div className='divider' /> : null}
            <Segment
              ref={segmentsRef.current[index]}
              index={index}
              value={segmentsValue[index]}
              onChange={handleOnInputChange}
              onPaste={handlePaste}
              onFocus={onFocus}
              className='control'
              name={`${name}-${index}-${Date.now()}`}
              {...data}
              {...props}
            />
          </Fragment>
        );
      }),
    [segmentsValue, props]
  );

  useEffect(() => {
    isValueIntialized.current = false;
  }, [divider, onChange, form?.setFieldTouched, form?.setFieldValue, name]);

  useEffect(() => {
    if (isValueIntialized.current === false) {
      isValueIntialized.current = true;
    } else {
      const newValue = Object.values(segmentsValue)
        .filter((entity) => !!entity)
        .join(divider);
      if (form !== undefined && name !== undefined) {
        form.setFieldTouched(name);
        form.setFieldValue(name, newValue);
      } else {
        onChange(newValue);
      }
    }
  }, [segmentsValue, divider, onChange, form?.setFieldTouched, form?.setFieldValue, name]);

  return <div css={styles}>{segmentList}</div>;
};
